import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDay } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import Button from 'components/Button';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import SingleCurrencyReview from './SingleCurrencyReview';
import MultiCurrencyReview from './MulticurrencyReview';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import SourceTransactionsContainer from './SourceTransactionsContainer';
import { Alert, Typography } from '@mui/material';
import { isAfter } from 'date-fns';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useDisclosure } from 'further-ui/hooks';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { Row } from 'further-ui/components/Layout';

type TransactionDetails = Transaction & {
  reconcilableTransactionId?: string;
  isRevaluationGainTransaction?: boolean;
  isUnrealisedGainOrLoss?: boolean;
};

type TransactionDetailsWithLcyValues = Omit<
  TransactionDetails,
  'amountExVatLcy' | 'amountIncVatLcy'
> & {
  amountExVatLcy: number;
  amountIncVatLcy: number;
};

type Props = {
  vehicleId: string;
  bankAccountId: string;
  bankOperation: {
    id: string;
    spent?: number;
    received?: number;
    accountCurrency: CurrencyCode;
    date: CalendarDay;
  };
  selectedTransactions: Array<{
    id: string;
    splitFromTransactionId?: string;
    unreconciledAmountTcy: number;
    operationType: ActionOperationType;
    currencyCode: CurrencyCode;
    actionRef: ActionRef;
  }>;
  onClose: () => void;
};

const Review: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankOperation,
  selectedTransactions,
  onClose,
}) => {
  const { vehicle } = useVehicle(vehicleId);
  const { hasEqualisationInDraftState } = useEqualisations(vehicleId);
  const confirmDialog = useDisclosure();

  const bankOperationAmountTcy =
    bankOperation.spent ?? bankOperation.received ?? 0;

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const [reconciliationDetails] =
    api.reconciliation.getDetails.useSuspenseQuery({
      vehicleId,
      bankAccountId,
      bankOperationId: bankOperation.id,
      selectedTransactions: selectedTransactions.map((transaction) => ({
        ...transaction,
        selectedAmount: transaction.unreconciledAmountTcy,
      })),
    });

  const [transactions, setTransactions] = useState<
    Array<Array<TransactionDetails>>
  >(
    'error' in reconciliationDetails
      ? []
      : [
          ...reconciliationDetails.transactions.map(
            (transaction) => transaction.transactionTypes,
          ),
          reconciliationDetails.foreignCurrencySaleGain.transactions,
        ],
  );
  const [
    unrealisedCashFxGainUnwindTransactions,
    setUnrealisedCashFxGainUnwindTransactions,
  ] = useState<Array<TransactionDetailsWithLcyValues>>(
    'error' in reconciliationDetails
      ? []
      : reconciliationDetails.unrealisedCashFxGainUnwind?.transactions ?? [],
  );

  const fxRate = useMemo(
    () =>
      'error' in reconciliationDetails
        ? null
        : reconciliationDetails.currencyRate
        ? reconciliationDetails.currencyRate.source
          ? {
              fetchedRate: reconciliationDetails.currencyRate.rate,
              fetchedSource: reconciliationDetails.currencyRate.source,
            }
          : {
              overrideRate: reconciliationDetails.currencyRate.rate,
              overrideSource: 'Reconciliation',
            }
        : null,
    [reconciliationDetails],
  );

  useEffect(() => {
    if ('error' in reconciliationDetails) return;

    setTransactions([
      ...reconciliationDetails.transactions.map(
        (transaction) => transaction.transactionTypes,
      ),
      reconciliationDetails.foreignCurrencySaleGain.transactions,
    ]);
  }, [reconciliationDetails]);

  const reconcileOperation =
    api.bankAccount.reconcileBankAccountOperation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Transaction reconciled.',
      }),
    );

  const handleReconcile = () => {
    reconcileOperation.mutate({
      vehicleId,
      bankAccountId,
      bankAccountOperationId: bankOperation.id,
      fxRate: fxRate ?? undefined,
      reconciledTransactions: selectedTransactions.map((transaction) => ({
        id: transaction.splitFromTransactionId ?? transaction.id,
        selectedAmount:
          transaction.operationType === ActionOperationType.Spent
            ? -transaction.unreconciledAmountTcy
            : transaction.unreconciledAmountTcy,
        actionRef: transaction.actionRef,
        operationType: transaction.operationType,
        currencyCode: transaction.currencyCode,
      })),
      transactions: transactions.flatMap((transactionsSet) =>
        transactionsSet.map((transaction) => ({
          ...transaction,
          amountIncVatLcy: transaction.amountIncVatLcy ?? 0,
          amountExVatLcy: transaction.amountExVatLcy ?? 0,
          transactionSubType: transaction.transactionSubType,
        })),
      ),
      unrealisedCashFxGainUnwindTransactions,
    });
  };

  const isMultiCurrency = selectedTransactions.some(
    (transaction) => transaction.currencyCode !== bankOperation.accountCurrency,
  );

  const handleChangeTransactions = (
    index: number,
    changedTransactions: Array<TransactionDetails>,
  ) => {
    const newTransactions = [...transactions];
    newTransactions[index] = changedTransactions;

    setTransactions(newTransactions);
  };

  if ('error' in reconciliationDetails) {
    return <Alert severity="error">{reconciliationDetails.error}</Alert>;
  }

  return (
    <>
      {isMultiCurrency && reconciliationDetails.isValid ? (
        <MultiCurrencyReview reconciliationDetails={reconciliationDetails} />
      ) : (
        <SingleCurrencyReview
          selectedTransactions={selectedTransactions}
          bankOperationAmountTcy={bankOperationAmountTcy}
          currency={bankOperation.accountCurrency}
        />
      )}
      {!!vehicle &&
        !transactionTypesLoading &&
        reconciliationDetails.isValid && (
          <>
            <Typography variant="h5">2. Review account transactions</Typography>
            {reconciliationDetails.transactions.map((transaction, index) => (
              <React.Fragment key={transaction.reconcilableTransactionId}>
                <TransactionAllocator
                  title={
                    isAfter(transaction.ledgerDate.endOfDay(), new Date())
                      ? 'Account transactions (prepayment)'
                      : 'Account transactions'
                  }
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={transactions[index] ?? []}
                  onChange={(changedTransactions) =>
                    handleChangeTransactions(index, changedTransactions)
                  }
                  transactionCurrency={transaction.transactionsCurrency}
                  vehicle={vehicle}
                  fxRate={fxRate}
                  disableRateChange={true}
                />
                {!!transaction.sourceTransactions.length && (
                  <SourceTransactionsContainer
                    showFutureTransactionsMessage={
                      !!transaction.sourceActionHasNoCurrencyRate
                    }
                    bankOperationDate={bankOperation.date}
                    sourceActionDate={transaction.sourceActionDate}
                  >
                    <TransactionAllocator
                      showCurrencyRate
                      transactionTypeOptions={transactionTypeOptions}
                      transactions={transaction.sourceTransactions}
                      onChange={() => {}}
                      transactionCurrency={transaction.transactionsCurrency}
                      vehicle={vehicle}
                      fxRate={transaction.sourceActionCurrencyRate ?? null}
                      readOnly
                    />
                  </SourceTransactionsContainer>
                )}
              </React.Fragment>
            ))}
            {!!reconciliationDetails.foreignCurrencySaleGain.transactions
              .length && (
              <TransactionAllocator
                title="Foreign currency sale gain or loss"
                transactionTypeOptions={transactionTypeOptions}
                transactions={transactions[transactions.length - 1]}
                onChange={(changedTransactions) =>
                  handleChangeTransactions(
                    transactions.length - 1,
                    changedTransactions,
                  )
                }
                transactionCurrency={bankOperation.accountCurrency}
                vehicle={vehicle}
                fxRate={fxRate}
                disableRateChange={true}
                forceShowTcy
              />
            )}
            {!!reconciliationDetails.unrealisedCashFxGainUnwind && (
              <TransactionAllocator
                title="Unrealised cash fx gain unwind"
                transactionTypeOptions={transactionTypeOptions}
                transactions={unrealisedCashFxGainUnwindTransactions}
                onChange={(transactions) => {
                  setUnrealisedCashFxGainUnwindTransactions(transactions);
                }}
                transactionCurrency={bankOperation.accountCurrency}
                vehicle={vehicle}
                disableRateChange={true}
                forceShowTcy
                fxRate={null}
              />
            )}
            <Row spacing="sm" fullWidth justify="flex-end">
              <Button variant="outlined" size="large" onClick={onClose}>
                Cancel and close
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={
                  hasEqualisationInDraftState
                    ? confirmDialog.onOpen
                    : handleReconcile
                }
                loading={reconcileOperation.isPending}
              >
                Reconcile
              </Button>
            </Row>
            {hasEqualisationInDraftState && (
              <PendingDraftEqualisationsModal
                isOpen={confirmDialog.isOpen}
                onClose={confirmDialog.onClose}
                onConfirm={handleReconcile}
                loading={reconcileOperation.isPending}
              />
            )}
          </>
        )}
    </>
  );
};

export default Review;
